/**
 * Care tab. Mini PRD: capture tile + journey-triggered prompts at the moment
 * of relevance (POA journey done -> "photograph your LPA").
 */
import { useNavigation } from '@/hooks/useNavigationContext';
import { Stack, Typography, Box } from '@mui/material';

import { PageTemplate } from '@karehero/llama/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';
import { Card } from '@karehero/llama/components/atoms/Card/Card';
import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import CaptureTile from './components/CaptureTile';
import { palette } from './components/palette';
import { useCaptureFlow } from './CaptureFlowContext';

export const CareTab = () => {
  const navigate = useNavigation();
  const { setEntrySurface, track } = useCaptureFlow();

  return (
    <PageTemplate
      maxWidth='sm'
      headerSlot={
        <PageHeader
          title='How can we help you today?'
          description="Whether you're learning or ready to act, we're here to help."
        />
      }
      contentSlot={
        <Stack spacing={2}>
          {/* Journey-triggered prompt: the distribution mechanism the Mini PRD
              calls out (tiles alone are not a plan). */}
          <Box
            sx={{
              backgroundColor: palette.softYellow,
              borderRadius: 3,
              padding: 2.5,
            }}
          >
            <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
              <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
                <Icon icon='party-horn' fontSize={18} />
                <Typography sx={{ fontSize: 15 }}>
                  You finished your Power of Attorney journey
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 14, color: palette.ink }}>
                Photograph your registered LPA and Keni will check what to do
                next - sharing it, storing it, and who to tell.
              </Typography>
              <Button
                ariaLabel='Photograph your LPA'
                variant='secondary'
                onPress={() => {
                  setEntrySurface('journey-prompt');
                  track('Capture Entry Tapped (proposed)', {
                    surface: 'journey-prompt',
                    journey: 'poa',
                  });
                  navigate('/capture');
                }}
              >
                Photograph your LPA
              </Button>
            </Stack>
          </Box>

          <CaptureTile surface='care' />

          <Card isFullWidth isPaddingSm>
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
              <Icon icon='book-open' fontSize={20} />
              <Stack>
                <Typography sx={{ fontSize: 14 }}>
                  What is Power of Attorney?
                </Typography>
                <Typography sx={{ color: palette.mutedText, fontSize: 12 }}>
                  A quick overview of LPAs and how they work.
                </Typography>
              </Stack>
            </Stack>
          </Card>

          <Card isFullWidth isPaddingSm>
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
              <Icon icon='shield-check' fontSize={20} />
              <Stack>
                <Typography sx={{ fontSize: 14 }}>
                  Why set up Power of Attorney?
                </Typography>
                <Typography sx={{ color: palette.mutedText, fontSize: 12 }}>
                  Why it matters and what happens without one.
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Stack>
      }
    />
  );
};

export default CareTab;
